/**
 * FileMenu — menu déroulant générique de la barre d'outils (« Fichier »,
 * « Ajout », …).
 *
 * Regroupe des actions qui encombraient la barre / la colonne de gauche.
 * Chaque entrée déclenche l'action ou la modale qui existe déjà ; ce composant
 * ne fait que l'UI du menu (bouton + dropdown organisé en sections).
 *
 * @param {Array<{title:string, items:Array<{label, icon, onClick, shortcut?, accent?}>}>} sections
 * @param {string} [label="Fichier"] — libellé du bouton
 * @param {JSX.Element} [icon] — icône du bouton (défaut : lignes de menu)
 */
import { useState, useRef, useEffect } from 'react';

const DEFAULT_ICON = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <line x1="4" y1="7" x2="20" y2="7" />
        <line x1="4" y1="12" x2="20" y2="12" />
        <line x1="4" y1="17" x2="14" y2="17" />
    </svg>
);

export default function FileMenu({ sections, label = 'Fichier', icon = DEFAULT_ICON }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setOpen((o) => !o)}
                title={label}
                className={`flex items-center gap-2 pl-3 pr-2.5 py-1.5 rounded-xl border transition-all ${
                    open
                        ? 'bg-white/10 border-white/15 text-white'
                        : 'bg-black/20 border-white/5 text-white/60 hover:text-white hover:bg-white/10'
                }`}
            >
                {icon}
                {/* lf-menu-label : masqué quand la barre devient étroite
                    (container query dans globals.css). Le nom reste accessible
                    par l'infobulle du bouton. */}
                <span className="lf-menu-label text-[10px] font-bold uppercase tracking-widest pt-0.5">{label}</span>
                {/* Chevron */}
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"
                     className={`transition-transform ${open ? 'rotate-180' : ''}`}>
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>

            {open && (
                <div className="absolute left-0 top-full mt-2 w-64 bg-forge-bg border border-white/10 rounded-xl shadow-2xl z-50 py-2 overflow-hidden">
                    {sections.map((sec, si) => (
                        <div key={sec.title}>
                            {si > 0 && <div className="my-1.5 border-t border-white/5" />}
                            <div className="px-3 pb-1 pt-0.5 text-[8px] font-mono uppercase tracking-[0.2em] text-white/30">
                                {sec.title}
                            </div>
                            {sec.items.map((item) => (
                                <button
                                    key={item.label}
                                    onClick={() => { setOpen(false); item.onClick?.(); }}
                                    className={`w-full flex items-center gap-3 px-3 py-1.5 text-left transition-colors group ${
                                        item.accent
                                            ? 'text-white/70 hover:text-orange-300 hover:bg-orange-500/10'
                                            : 'text-white/70 hover:text-white hover:bg-white/10'
                                    }`}
                                >
                                    <span className="w-4 h-4 flex items-center justify-center text-white/45 group-hover:text-current">
                                        {item.icon}
                                    </span>
                                    <span className="flex-1 text-[12px] font-sans">{item.label}</span>
                                    {item.shortcut && (
                                        <span className="text-[9px] font-mono text-white/25">{item.shortcut}</span>
                                    )}
                                </button>
                            ))}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
